import React from "react";

import localForage from "localforage";
import { createRoot } from "react-dom/client";
import TagManager from "react-gtm-module";
import { RawIntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router/dom";
import { StyleSheetManager } from "styled-components";

import AppErrorBoundary from "Components/AppErrorBoundary";
import { FullPageRedirect } from "Components/FullPageRedirect";
import config from "console_config";
import { ANALYTICS_ENABLED } from "Constants/constants";
import { getIntl } from "Libs/intl";

import { setTracking } from "./root/pages/utils/cookie";
import {
  SIGNUP_TARGET_PARAM,
  rememberDispatchSignup
} from "./root/pages/utils/dispatchSignup";
import { trackRouterLocation } from "./root/router/trackRouterLocation";
import authRoutes from "./routes.auth";
import consoleRoutes from "./routes.console";
import configureAppStore from "./store/configureStore";

import("Vendor_resources/images/favicon.ico");

// Conduct a hard refresh of the page if one of the assets bundled by Vite
// fails to load. This is needed in the event a cached version of page is currently
// loaded but the assets are no longer available because of a rebuild+deployment.
window.addEventListener("vite:preloadError", async () => {
  const hasRefreshed = await localForage.getItem("hasRefreshedAfterError");
  if (hasRefreshed) {
    await localForage.removeItem("hasRefreshedAfterError");
    return;
  }
  await localForage.setItem("hasRefreshedAfterError", true);
  window.location.reload(true);
});

// If on Auth UI, store the tracking on a singleton
// to forward it in the request to the Auth Server
if (window.location.origin === config.URL_AUTH) {
  const urlParams = new URLSearchParams(window.location.search);
  // Before the filter below, which drops `email_address` (DISP-1185).
  rememberDispatchSignup(urlParams);

  // Mirrors the annotation params the Auth server records on signup
  // (`AllParams` in sarsen's annotations/store/model.go).
  urlParams.forEach((v, k) => {
    if (
      k !== "_gclid" &&
      !k.startsWith("_utm_") &&
      k !== "trial_type" &&
      k !== SIGNUP_TARGET_PARAM
    ) {
      urlParams.delete(k);
    }
  });

  setTracking(urlParams);
}

if (ANALYTICS_ENABLED) {
  const tagManagerArgs = {
    gtmId: config.ID_GOOGLE_TAG_MANAGER,
    cookieDomain: config.CUSTOM_COOKIE_DOMAIN
  };
  TagManager.initialize(tagManagerArgs);
}

const store = configureAppStore();
const isAuth = window.location.origin === config.URL_AUTH;
const appContainer = document.getElementById("app");
const intl = getIntl();
const fallbackContainer = document.getElementById("fallback");
const contactLink = document.querySelector("a.main-error-cta");

setTimeout(() => {
  if (appContainer.hasChildNodes()) {
    fallbackContainer?.remove();
  }
  const pageErrorCtaLabel = intl.messages["error.main_page_cta_label"];
  if (contactLink && pageErrorCtaLabel) {
    contactLink.textContent = pageErrorCtaLabel;
  }
}, 10000);

if (config.ID_PLATFORM_TREE) {
  //  eslint-disable-next-line no-console
  console.log(`Build ID: %c${config.ID_PLATFORM_TREE}`, "color: green");
}

const root = createRoot(appContainer);
// Disables stripe on the landing page redirection for the brand merger
const shouldLoadStripe =
  !config.CUSTOM_FULL_PAGE_REDIRECT && !isAuth && config.ENABLE_STRIPE;

if (shouldLoadStripe) {
  const script = document.createElement("script");
  script.src = "https://js.stripe.com/v3/";
  document.body.appendChild(script);
}

if (
  config.CUSTOM_FULL_PAGE_REDIRECT &&
  !["/sso/signup", "/sso/tfa", "/sso/recover", "/country"].includes(
    location.pathname
  )
) {
  root.render(<FullPageRedirect />);
} else {
  // Console only — the auth app has no route history. Module scope reads the
  // synchronously-matched initial state and sidesteps the StrictMode
  // double-subscribe a useEffect would incur. Unsubscribe is intentionally
  // discarded: the subscription lives for the app's lifetime.
  if (!isAuth) {
    void trackRouterLocation(consoleRoutes, store);
  }

  root.render(
    // disableCSSOMInjection avoids IndexSizeError when third-party scripts
    // (Pendo) mutate the stylesheet styled-components v5 manages via
    // insertRule/deleteRule. Remove once styled-components v6 lands.
    <StyleSheetManager disableCSSOMInjection>
      <AppErrorBoundary intl={intl}>
        <Provider store={store}>
          <RawIntlProvider value={intl}>
            <React.Suspense fallback={<div />}>
              <RouterProvider router={isAuth ? authRoutes : consoleRoutes} />
            </React.Suspense>
          </RawIntlProvider>
        </Provider>
      </AppErrorBoundary>
    </StyleSheetManager>
  );
}
